/**
 * Hooks de datos del **portal del cliente**.
 *
 * Las claves de React Query van bajo `portal:` para no mezclarse con las del
 * panel (mismo `QueryClient`, sesiones distintas).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { portalApi } from './portal.api';
import { usePortalStore } from './portal.store';
import type {
  BalancePortal,
  ConductorFormulario,
  ConductorPortal,
  ConsumoPortal,
  DashboardPortal,
  DespachoEnVivoPortal,
  DespachoPortal,
  FacturaDetallePortal,
  FacturaPortal,
  LineaCreditoPortal,
  MensajeFormulario,
  MensajePortal,
  MovimientoPortal,
  VehiculoFormulario,
  VehiculoPortal,
} from './portal.types';

export const portalKeys = {
  all: ['portal'] as const,
  dashboard: ['portal', 'dashboard'] as const,
  facturas: (filtros: unknown) => ['portal', 'facturas', filtros] as const,
  factura: (id: number) => ['portal', 'factura', id] as const,
  consumos: (filtros: unknown) => ['portal', 'consumos', filtros] as const,
  creditos: ['portal', 'creditos'] as const,
  balance: (filtros: unknown) => ['portal', 'balance', filtros] as const,
  movimientos: (filtros: unknown) => ['portal', 'movimientos', filtros] as const,
  vehiculos: ['portal', 'vehiculos'] as const,
  conductores: ['portal', 'conductores'] as const,
  despachos: ['portal', 'despachos'] as const,
  despachoEnVivo: (lado: number, transaccion: number) =>
    ['portal', 'despacho-en-vivo', lado, transaccion] as const,
  mensajes: ['portal', 'mensajes'] as const,
} as const;

/** Resumen del portal (KPIs, últimas listas y consumos por mes). */
export function usePortalDashboard() {
  return useQuery({
    queryKey: portalKeys.dashboard,
    queryFn: ({ signal }) => portalApi.dashboard(signal),
    staleTime: 60_000,
  });
}

export function usePortalFacturas(filtros: {
  desde?: string;
  hasta?: string;
  serie?: string;
  numero?: string;
  placa?: string;
  origen?: string;
}) {
  return useQuery<{ total: number; playa: number; administrativas: number; facturas: FacturaPortal[] }>({
    queryKey: portalKeys.facturas(filtros),
    queryFn: ({ signal }) => portalApi.facturas(filtros, signal),
    staleTime: 30_000,
  });
}

export function usePortalFacturaDetalle(id: number | null) {
  return useQuery<{ factura: FacturaDetallePortal }>({
    queryKey: portalKeys.factura(id ?? 0),
    queryFn: ({ signal }) => portalApi.facturaDetalle(id as number, signal),
    enabled: id !== null && id > 0,
  });
}

export function usePortalConsumos(filtros: { desde?: string; hasta?: string }) {
  return useQuery<{ total: number; total_monto: number; consumos: ConsumoPortal[] }>({
    queryKey: portalKeys.consumos(filtros),
    queryFn: ({ signal }) => portalApi.consumos(filtros, signal),
    staleTime: 30_000,
  });
}

export function usePortalCreditos() {
  return useQuery<{ lineas: LineaCreditoPortal[]; facturas_pendientes: DashboardPortal['facturas_pendientes'] }>({
    queryKey: portalKeys.creditos,
    queryFn: ({ signal }) => portalApi.creditos(signal),
    staleTime: 60_000,
  });
}

export function usePortalBalance(filtros: { desde?: string; hasta?: string }) {
  return useQuery<BalancePortal>({
    queryKey: portalKeys.balance(filtros),
    queryFn: ({ signal }) => portalApi.balance(filtros, signal),
    staleTime: 30_000,
  });
}

export function usePortalMovimientos(filtros: { desde?: string; hasta?: string }) {
  return useQuery<{ total: number; movimientos: MovimientoPortal[] }>({
    queryKey: portalKeys.movimientos(filtros),
    queryFn: ({ signal }) => portalApi.movimientos(filtros, signal),
    staleTime: 30_000,
  });
}

/** Cierra la sesión del portal (avisa a la API y limpia el store local). */
export function useSalirDelPortal() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: () => portalApi.cerrarSesion().catch(() => ({ mensaje: 'Sesión cerrada.' })),
    onSettled: () => {
      usePortalStore.getState().cerrarSesion();
      consulta.removeQueries({ queryKey: portalKeys.all });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Vehículos
 * ------------------------------------------------------------------ */

export function usePortalVehiculos() {
  return useQuery<{ vehiculos: VehiculoPortal[] }>({
    queryKey: portalKeys.vehiculos,
    queryFn: ({ signal }) => portalApi.vehiculos(signal),
    staleTime: 60_000,
  });
}

/** Alta y edición de vehículos (la edición no toca la placa). */
export function useGuardarVehiculo() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id?: number; datos: VehiculoFormulario }) =>
      variables.id
        ? portalApi.actualizarVehiculo(variables.id, variables.datos)
        : portalApi.crearVehiculo(variables.datos),
    onSuccess: () => {
      void consulta.invalidateQueries({ queryKey: portalKeys.vehiculos });
      void consulta.invalidateQueries({ queryKey: portalKeys.dashboard });
    },
  });
}

/** Baja lógica de un vehículo. */
export function useInhabilitarVehiculo() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => portalApi.inhabilitarVehiculo(id),
    onSuccess: () => {
      void consulta.invalidateQueries({ queryKey: portalKeys.vehiculos });
      void consulta.invalidateQueries({ queryKey: portalKeys.dashboard });
    },
  });
}

/* ------------------------------------------------------------------ *
 * Conductores
 * ------------------------------------------------------------------ */

export function usePortalConductores() {
  return useQuery<{ conductores: ConductorPortal[] }>({
    queryKey: portalKeys.conductores,
    queryFn: ({ signal }) => portalApi.conductores(signal),
    staleTime: 60_000,
  });
}

/** Alta y edición de conductores (el documento sólo se fija en el alta). */
export function useGuardarConductor() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id?: number; datos: ConductorFormulario }) =>
      variables.id
        ? portalApi.actualizarConductor(variables.id, variables.datos)
        : portalApi.crearConductor(variables.datos),
    onSuccess: () => void consulta.invalidateQueries({ queryKey: portalKeys.conductores }),
  });
}

/** Baja lógica de un conductor. */
export function useInhabilitarConductor() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => portalApi.inhabilitarConductor(id),
    onSuccess: () => void consulta.invalidateQueries({ queryKey: portalKeys.conductores }),
  });
}

/* ------------------------------------------------------------------ *
 * Despachos
 * ------------------------------------------------------------------ */

export function usePortalDespachos() {
  return useQuery<{ total: number; despachos: DespachoPortal[] }>({
    queryKey: portalKeys.despachos,
    queryFn: ({ signal }) => portalApi.despachos(signal),
    // Los despachos en curso cambian solos: se refrescan al volver a la pantalla.
    staleTime: 15_000,
  });
}

/**
 * Lectura en vivo del controlador para un despacho concreto.
 *
 * Sólo se consulta cuando el despacho tiene `transaccion` y `lado_id`: los
 * despachos ya cerrados en el sistema no tienen transacción PTS que consultar.
 */
export function usePortalDespachoEnVivo(lado: number | null, transaccion: number | null) {
  return useQuery<{ despacho: DespachoEnVivoPortal }>({
    queryKey: portalKeys.despachoEnVivo(lado ?? 0, transaccion ?? 0),
    queryFn: ({ signal }) => portalApi.despachoEnVivo(lado as number, transaccion as number, signal),
    enabled: lado !== null && transaccion !== null && lado > 0 && transaccion > 0,
    retry: false,
  });
}

/* ------------------------------------------------------------------ *
 * Mensajes
 * ------------------------------------------------------------------ */

export function usePortalMensajes() {
  return useQuery<{ total: number; sin_leer: number; mensajes: MensajePortal[] }>({
    queryKey: portalKeys.mensajes,
    queryFn: ({ signal }) => portalApi.mensajes(signal),
    staleTime: 30_000,
  });
}

/** Envía un mensaje a la estación. */
export function useEnviarMensaje() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (datos: MensajeFormulario) => portalApi.enviarMensaje(datos),
    onSuccess: () => void consulta.invalidateQueries({ queryKey: portalKeys.mensajes }),
  });
}

/** Marca como leída una respuesta de la estación. */
export function useMarcarMensajeLeido() {
  const consulta = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => portalApi.marcarMensajeLeido(id),
    onSuccess: () => void consulta.invalidateQueries({ queryKey: portalKeys.mensajes }),
  });
}
